














































































































































































import Vue from 'vue';
import CSPService, {
    CandidateSchool,
    CandidateStudyProgram,
    StudyField,
    StudyLevel,
    StudyProgram,
} from '~/services/CSPService';
import { isAfter } from 'date-fns';

// Components
import YearMonthPicker from '~/components/builders/YearMonthPicker.vue';
import TextFieldSkeleton from '~/components/skeletons/TextFieldSkeleton.vue';

// Types
interface StudyProgramDetails {
    schoolId?: ValueLabel;

    studyProgramId?: ValueLabel;

    campusId?: ValueLabel;

    studyLevelId?: ValueLabel;
    studyFieldId?: ValueLabel;

    studyStart?: Date;
    studyEnd?: Date;
}

interface FormErrors {
    schoolId?: string;
    studyProgramId?: string;
    studyStart?: string;
    studyEnd?: string;
}

interface ValueLabel {
    value: string | number | null;
    label: string;
}

enum LoadingLevel {
    None = 0,
    StudyProgram = 900,
    School = 1000,
    All = 10000,
}

enum Steps {
    School = 1000,
    StudyProgram = 900,
    Campus = 800,
    StudyFieldsAndLevel = 700,
    StartAndEnd = 600,
    Done = 0,
}

const name = <T>(name: keyof T) => name;

export default Vue.extend({
    name: 'StudyProgramForm',
    components: { YearMonthPicker, TextFieldSkeleton },
    props: {
        disableActions: {
            type: Boolean,
            default: false,
        },
        candidateStudyProgram: {
            type: Object as () => CandidateStudyProgram,
            default: null,
        },
    },
    data: () => {
        return {
            Steps,
            LoadingLevel,
            loadingLevel: LoadingLevel.None,

            cspService: new CSPService(),
            studyProgramDetails: {} as StudyProgramDetails,

            schools: [] as CandidateSchool[],
            schoolStudyPrograms: [] as StudyProgram[],

            studyLevels: [] as StudyLevel[],
            studyFields: [] as StudyLevel[],
        };
    },
    computed: {
        //
        // ValueLabel methods:
        //
        schoolsValueLabel(): ValueLabel[] {
            return this.schools.map(school => ({
                value: school.id,
                label: school.name,
            }));
        },
        studyProgramsValueLabel(): ValueLabel[] {
            return this.schoolStudyPrograms.map(studyProgram => {
                const label = !!studyProgram.level
                    ? `${studyProgram.name} (${studyProgram.level.name})`
                    : studyProgram.name;
                return {
                    value: studyProgram.id,
                    label,
                };
            });
        },
        schoolCampusesValueLabel(): ValueLabel[] {
            if (!this.studyProgramDetails.studyProgramId) {
                return [];
            }
            const studyProgram = this.schoolStudyPrograms.find(studyProgram => {
                return studyProgram.id === this.studyProgramDetails.studyProgramId!!.value;
            });
            if (!studyProgram || !studyProgram.campuses) {
                return [];
            }

            return studyProgram.campuses.map(campus => ({
                value: campus.id,
                label: campus.name,
            }));
        },
        studyLevelsValueLabel(): ValueLabel[] {
            return this.studyLevels.map(studyLevel => ({
                value: studyLevel.id,
                label: studyLevel.name,
            }));
        },
        studyFieldsValueLabel(): ValueLabel[] {
            return this.studyFields.map(studyField => ({
                value: studyField.id,
                label: studyField.name,
            }));
        },

        //
        // Core
        //
        step(): Steps {
            if (!this.studyProgramDetails.schoolId) {
                return Steps.School;
            } else if (!this.studyProgramDetails.studyProgramId) {
                return Steps.StudyProgram;
            } else if (
                this.studyProgramDetails.studyProgramId.value !== null &&
                !this.studyProgramDetails.campusId &&
                this.schoolCampusesValueLabel.length > 1
            ) {
                return Steps.Campus;
            } else if (
                this.studyProgramDetails.studyProgramId.value === null &&
                (!this.studyProgramDetails.studyFieldId || !this.studyProgramDetails.studyLevelId)
            ) {
                return Steps.StudyFieldsAndLevel;
            } else if (!this.studyProgramDetails.studyStart || !this.studyProgramDetails.studyEnd) {
                return Steps.StartAndEnd;
            } else {
                return Steps.Done;
            }
        },
        isUnverifiedMode(): boolean {
            // isUnverifiedMode is true when the study program is custom and unverified.
            // That means that there is no schoolId or the studyProgram is custom.
            return Boolean(
                (this.studyProgramDetails.schoolId &&
                    this.studyProgramDetails.schoolId.value === null) ||
                    (this.studyProgramDetails.studyProgramId &&
                        (this.studyProgramDetails.studyProgramId.value === null ||
                            !this.schoolStudyPrograms.find(
                                program =>
                                    this.studyProgramDetails &&
                                    this.studyProgramDetails.studyProgramId &&
                                    program.id === this.studyProgramDetails.studyProgramId.value
                            )))
            );
        },
        isEditMode(): boolean {
            return Boolean(this.candidateStudyProgram && this.candidateStudyProgram.id);
        },
        formErrors(): FormErrors {
            const errors: FormErrors = {};

            if (
                this.studyProgramDetails.schoolId &&
                this.studyProgramDetails.schoolId.value === null &&
                (this.studyProgramDetails.schoolId.label || '').length < 2
            ) {
                errors.schoolId = this.$t('studyProgram.form.errors.schoolId') as string;
            }
            if (
                this.studyProgramDetails.studyProgramId &&
                this.studyProgramDetails.studyProgramId.value === null &&
                (this.studyProgramDetails.studyProgramId.label || '').length < 2
            ) {
                errors.studyProgramId = this.$t(
                    'studyProgram.form.errors.studyProgramId'
                ) as string;
            }
            if (
                this.studyProgramDetails.studyStart &&
                this.studyProgramDetails.studyEnd &&
                isAfter(this.studyProgramDetails.studyStart, this.studyProgramDetails.studyEnd)
            ) {
                errors.studyStart = this.$t('studyProgram.form.errors.studyStart') as string;
            }

            return errors;
        },
        isFormValid(): boolean {
            return Boolean(
                Object.values(this.formErrors).filter(it => it).length === 0 &&
                    this.studyProgramDetails.schoolId &&
                    this.studyProgramDetails.studyProgramId &&
                    (this.studyProgramDetails.studyProgramId.value === null ||
                        this.schoolCampusesValueLabel.length === 0 ||
                        this.studyProgramDetails.campusId) &&
                    this.studyProgramDetails.studyStart &&
                    this.studyProgramDetails.studyEnd
            );
        },
    },
    methods: {
        //
        // Load data methods
        //
        loadSchools(): Promise<CandidateSchool[]> {
            return this.cspService.getSchools().then(schools => {
                this.schools = schools;
                return schools;
            });
        },
        loadStudyLevels(): Promise<StudyLevel[]> {
            return this.cspService.getStudyLevels().then(studyLevels => {
                this.studyLevels = studyLevels;
                return studyLevels;
            });
        },
        loadStudyFields(): Promise<StudyField[]> {
            return this.cspService.getStudyFields().then(studyFields => {
                this.studyFields = studyFields;
                return studyFields;
            });
        },
        loadSchoolStudyPrograms(schoolId: number): Promise<StudyProgram[]> {
            return this.cspService.getSchoolStudyPrograms(schoolId).then(studyPrograms => {
                this.schoolStudyPrograms = studyPrograms;
                return studyPrograms;
            });
        },
        load(): Promise<void> {
            this.loadingLevel = LoadingLevel.School;
            return Promise.all([this.loadSchools(), this.loadStudyLevels(), this.loadStudyFields()])
                .then(() => {
                    return this.loadandMapInput();
                })
                .finally(() => {
                    this.loadingLevel = LoadingLevel.None;
                });
        },

        //
        // Setters
        //
        setSchoolByValueLabel(value?: ValueLabel): void {
            this.$set(this.studyProgramDetails, 'schoolId', value);

            this.schoolStudyPrograms = [];
            if (value && value.value !== null) {
                const schoolId = value.value;
                this.loadingLevel = LoadingLevel.StudyProgram;
                this.loadSchoolStudyPrograms(schoolId as number).finally(() => {
                    this.focusInput('study-program-input');
                    this.loadingLevel = LoadingLevel.None;
                });
            }
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('studyProgramId'), null);
            this.$set(this.studyProgramDetails, 'campusId', null);
        },
        setStudyProgramByValueLabel(value?: ValueLabel): void {
            // If the study program already exists, is not verified and only the name changes, we should not update the
            // 'studyProgramId', but just update the name.
            if (
                this.isEditMode &&
                this.candidateStudyProgram.studyProgram &&
                !this.candidateStudyProgram.studyProgram.isVerified &&
                !!value &&
                value.value === null &&
                value.label !== this.candidateStudyProgram.studyProgram.name &&
                this.studyProgramDetails.studyProgramId &&
                !!this.studyProgramDetails.studyProgramId.value
            ) {
                this.$set(this.studyProgramDetails, name<StudyProgramDetails>('studyProgramId'), {
                    value: this.studyProgramDetails.studyProgramId.value,
                    label: value.label,
                });
            } else {
                this.$set(
                    this.studyProgramDetails,
                    name<StudyProgramDetails>('studyProgramId'),
                    value
                );
            }

            if (value && value.value !== null) {
                // When there is only one campus, select it automatically.
                const studyProgramId = value.value;
                const studyProgram = this.schoolStudyPrograms.find(sp => sp.id === studyProgramId);
                if (studyProgram && studyProgram.campuses && studyProgram.campuses.length === 1) {
                    this.$set(this.studyProgramDetails, name<StudyProgramDetails>('campusId'), {
                        value: studyProgram.campuses[0].id,
                        label: studyProgram.campuses[0].name,
                    } as ValueLabel);
                    return;
                } else {
                    this.focusInput('campus-input');
                }
            }
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('campusId'), null);
        },
        setCampusByValueLabel(value?: ValueLabel): void {
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('campusId'), value);
        },
        setStudyLevelByValueLabel(value?: ValueLabel): void {
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('studyLevelId'), value);
        },
        setStudyFieldByValueLabel(value?: ValueLabel): void {
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('studyFieldId'), value);
        },
        setStudyStart(value?: Date): void {
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('studyStart'), value);
        },
        setStudyEnd(value?: Date): void {
            this.$set(this.studyProgramDetails, name<StudyProgramDetails>('studyEnd'), value);
        },

        //
        // Core
        //
        buildCandidateStudyProgram(): CandidateStudyProgram {
            let studyProgram: StudyProgram = {} as StudyProgram;

            if (
                (!this.isUnverifiedMode && this.studyProgramDetails.schoolId!!.value) ||
                this.studyProgramDetails.campusId
            ) {
                const studyProgramOrUndefined = this.schoolStudyPrograms.find(
                    sp => sp.id === this.studyProgramDetails.studyProgramId!!.value
                );

                if (studyProgramOrUndefined === undefined) {
                    throw new Error(
                        `Unable to find verified study program with id: ${
                            this.studyProgramDetails.studyProgramId!.value
                        }`
                    );
                }
                studyProgram = studyProgramOrUndefined;
            } else {
                let studyProgramId = undefined;
                if (this.studyProgramDetails && this.studyProgramDetails.studyProgramId) {
                    studyProgramId = this.studyProgramDetails.studyProgramId.value;
                }

                const hasSchool = Boolean(
                    this.studyProgramDetails &&
                        this.studyProgramDetails.schoolId &&
                        this.studyProgramDetails.schoolId.value
                );

                studyProgram = {
                    id: studyProgramId,
                    schoolId: hasSchool ? this.studyProgramDetails.schoolId!.value : 0, // SchoolId is 0 for custom study programs.
                    customSchoolName: !hasSchool
                        ? this.studyProgramDetails.schoolId!.label
                        : undefined,

                    name: this.studyProgramDetails.studyProgramId!!.label,

                    isVerified: false,

                    level: this.studyLevels.find(
                        it => it.id === this.studyProgramDetails.studyLevelId!!.value
                    )!!,
                    levelId: this.studyProgramDetails.studyLevelId!!.value as string,
                    fields: [
                        this.studyFields.find(
                            it => it.id === this.studyProgramDetails.studyFieldId!!.value
                        )!!,
                    ],
                } as StudyProgram;
            }

            const campusIdValueLabel: ValueLabel | undefined = this.studyProgramDetails.campusId;
            const candidateStudyProgram: CandidateStudyProgram = {
                ...(this.candidateStudyProgram || {}),
                programId: studyProgram.id,
                start: this.studyProgramDetails.studyStart!!,
                end: this.studyProgramDetails.studyEnd!!,

                campusId: campusIdValueLabel ? (campusIdValueLabel.value as string) : undefined,
                campus:
                    campusIdValueLabel && studyProgram.campuses
                        ? studyProgram.campuses.find(c => c.id === campusIdValueLabel.value)
                        : undefined,

                studyProgram: studyProgram,
            };
            return candidateStudyProgram;
        },
        clear() {
            this.studyProgramDetails = {};
        },
        async loadandMapInput(): Promise<void> {
            const csp = this.candidateStudyProgram;
            if (!csp) {
                return;
            }
            const studyProgram = csp.studyProgram;
            if (!studyProgram) {
                return;
            }

            const details = {} as StudyProgramDetails;

            // Set school
            if (studyProgram.schoolId && studyProgram.schoolId !== 0) {
                const school = this.schools.find(it => it.id === studyProgram.schoolId);

                await this.loadSchoolStudyPrograms(studyProgram.schoolId);

                details.schoolId = {
                    value: school!!.id,
                    label: school!!.name,
                } as ValueLabel;
            } else {
                details.schoolId = {
                    value: null,
                    label: studyProgram.customSchoolName,
                } as ValueLabel;
            }

            // Set study program
            details.studyProgramId = {
                value: studyProgram.id || null,
                label: studyProgram.name,
            } as ValueLabel;

            // Set campus
            if (csp.campusId) {
                const studyProgram = this.schoolStudyPrograms.find(it => it.id === csp.programId)!!;
                if (studyProgram && studyProgram.campuses && studyProgram.campuses.length > 0) {
                    const campus = studyProgram.campuses.find(it => it.id === csp.campusId);
                    if (campus) {
                        details.campusId = {
                            value: campus.id,
                            label: campus.name,
                        } as ValueLabel;
                    }
                }
            }

            // Set study level
            const level = this.studyLevels.find(it => it.id === studyProgram.levelId);
            if (level) {
                details.studyLevelId = {
                    value: level.id,
                    label: level.name,
                } as ValueLabel;
            }

            // Set study field
            if (studyProgram.fields && studyProgram.fields.length > 0) {
                const field = this.studyFields.find(it => it.id === studyProgram.fields!![0].id);
                if (field) {
                    details.studyFieldId = {
                        value: field.id,
                        label: field.name,
                    } as ValueLabel;
                }
            }

            details.studyStart = new Date(csp.start);
            details.studyEnd = new Date(csp.end);

            this.studyProgramDetails = details;
        },

        //
        // Actions
        //
        handleSaveOrUpdate() {
            if (!this.isFormValid) {
                return;
            }

            const data = this.buildCandidateStudyProgram();

            if (!this.candidateStudyProgram) {
                this.$emit('create', data);
            } else {
                this.$emit('save', data);
            }

            this.onClose();
        },
        onDelete() {
            this.$emit('delete', this.candidateStudyProgram);
            this.onClose();
        },
        onClose() {
            this.clear();
            this.$emit('close');
        },

        //
        // MISC
        //
        focusInput(input: string) {
            setTimeout(() => {
                const el = this.$refs[input] as HTMLInputElement;
                if (el && el.focus) {
                    el.focus();
                }
            }, 100);
        },
    },
    mounted() {
        this.load();
    },
});
